import type { SearchAddressResult } from '$lib/components/search/address/types';
import type { WayPoint } from '$lib/components/search/itinerary/types';
import type { Result } from '$lib/components/search/types';
import type { SearchInputEventPayload } from '$lib/services/tracking/googleAnalytics/events';
import { track } from '$lib/services/tracking/googleAnalytics';

interface SearchbarAutocompletionEventPayload {
	result?: string;
	country?: string;
	city?: string;
	zipcode?: string;
	from?: string;
	historical_search?: string;
}

interface BuildSearchbarAutocompletionPayloadArgs {
	selected: SearchAddressResult | null;
	isFromStore: boolean;
	from?: WayPoint;
}

interface BuildSearchbarUniquePayloadArgs {
	inputValue?: string;
	results?: Result[] | null;
	resultsLength?: number ;
	searchId?: string;
	selected?: Result | null;
	isFromStore?: boolean;
}

export function buildSearchbarUniquePayload({ selected, results, searchId, inputValue, resultsLength, isFromStore = false }: BuildSearchbarUniquePayloadArgs): SearchInputEventPayload {
	return {
		search_input_result: selected ? JSON.stringify(selected) : undefined,
		search_input_results_json: !isFromStore && results ? JSON.stringify(results) : undefined,
		search_input_result_position: selected && results ? String(results.indexOf(selected)) : undefined,
		search_input_search_id: searchId ?? undefined,
		search_input_input: inputValue || undefined,
		search_input_nb_results: resultsLength ?? undefined,
	};
}

export function buildSearchbarAutocompletionPayload({
	selected,
	isFromStore,
	from,
}: BuildSearchbarAutocompletionPayloadArgs): SearchbarAutocompletionEventPayload {
	return {
		city: selected?.address?.city ?? undefined,
		country: selected?.address?.country?.code,
		zipcode: selected?.address?.zipCode ?? undefined,
		result: selected?.formattedName ?? undefined,
		historical_search: String(isFromStore),
		...(from && {
			from: from.isArrival ? 'arrival' : from.isDeparture ? 'departure' : `step${from.index}`,
		}),
	};
}

export function trackSearchbarAutocompletion(payload: SearchbarAutocompletionEventPayload) {
	track({
		action: 'searchbar_autocompletion',
		payload: {
			action: 'selection',
			...payload,
		},
	});
}

export function trackSearchInputClick(payload: SearchInputEventPayload) {
	track({
		action: 'search_input_click',
		payload,
	});
}

export function trackSearchInputResults(payload: SearchInputEventPayload) {
	track({
		action: 'search_input_results',
		payload,
	});
}

export function trackSearchInputSearch(payload: SearchInputEventPayload) {
	track({
		action: 'search_input_search',
		payload,
	});
}

export function trackSearchbarFocus() {
	track({
		action: 'searchbar',
		payload: {
			action: 'click',
		},
	});
}

export function trackSearchInputFocus(payload: SearchInputEventPayload) {
	track({
		action: 'search_input_focus',
		payload: {
			...payload,
			action: 'click',
		},
	});
}

export function trackSearchbarOnFocusUtils() {
	let trackingIsTriggered = false;

	function setFocusIsTriggered(state?: boolean) {
		trackingIsTriggered = state ?? !trackingIsTriggered;
	}

	return {
		setIsTriggered: setFocusIsTriggered,
		trackingIsTriggered,
	};
}
